import {
  CheckCircle2,
  CircleHelp,
  FileLock2,
  ShieldCheck,
  ShieldX,
  UserCheck,
  XCircle,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type { PendingApproval } from '@/api/approvals-types'
import { TabStrip, type TabDefinition } from '@/components/tab-strip'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Spinner } from '@/components/ui/spinner'
import { formatDateTime } from '@/lib/datetime'
import { cn } from '@/lib/utils'

import { ApprovalAmount, BranchCell, ExpiryBadge } from './approval-presentation'
import { ApprovalTrail } from './approval-trail'
import { DecisionDialog, type DecisionMode } from './decision-dialog'
import { describeExpiry, describeWait } from './expiry'
import { useApproveRequest, useRejectRequest } from './mutations'
import { APPROVAL_PERMISSIONS, useApprovalPermissions } from './permissions'
import { useApprovalMaker, useCheckerReach } from './queries'
import { describeRequestType } from './request-types'

/**
 * One request, opened.
 *
 * The screen a checker was promised is a side-by-side of what the maker submitted against the
 * current state of the thing it touches. That screen cannot be built against this API and this
 * panel says so rather than implying it: the stored command is held server-side as a hashed
 * payload and is never served, and the inbox row carries a one-line description and nothing else.
 * What *can* be given instead is everything about the decision itself — who raised it, when, out of
 * which branch, for how much, against which deadline — plus an honest pre-flight of the reasons
 * the engine could refuse the countersignature, so a checker learns them before pressing rather
 * than afterwards.
 *
 * It is a panel rather than its own route because the inbox is a queue: a checker works down it,
 * and coming back to the list is the common case rather than the exception. It reads on a phone —
 * the mobile policy names this screen as the strongest case for one, since dual control means an
 * absent approver stops other people working.
 */

const PANEL_ID = 'approval-detail-panel'

type DetailTab = 'request' | 'history'

type CheckState = 'ok' | 'blocked' | 'unknown' | 'pending'

interface PreflightCheck {
  key: string
  state: CheckState
  label: string
  detail: ReactNode
}

export interface ApprovalDetailProps {
  approval: PendingApproval | null
  onOpenChange: (open: boolean) => void
}

/**
 * Mount this with `key={approval.requestId}`.
 *
 * A different request is a different decision, and nothing about the last one — the open tab, a
 * refusal still on screen, the note half typed into a dialog — may carry over onto it. Remounting
 * is how that is guaranteed rather than remembered.
 */
export function ApprovalDetail({ approval, onOpenChange }: ApprovalDetailProps) {
  const permissions = useApprovalPermissions()
  const reach = useCheckerReach()

  const [tab, setTab] = useState<DetailTab>('request')
  const [dialog, setDialog] = useState<DecisionMode | null>(null)
  const [outcome, setOutcome] = useState<DecisionMode | null>(null)

  const approve = useApproveRequest()
  const reject = useRejectRequest()

  const maker = useApprovalMaker(
    approval?.makerUsername ?? '',
    approval !== null && permissions.canViewStaff,
  )

  if (!approval) {
    return null
  }

  const descriptor = describeRequestType(approval.requestType)
  const expiry = describeExpiry(approval.expiresAtUtc)
  const canApprove = permissions.canApprove(approval.requestType)

  const branchInReach = reach.canAccessAllBranches
    ? 'ok'
    : approval.isInstitutionWide
      ? 'blocked'
      : reach.branches.some((branch) => branch.id === approval.branchId)
        ? 'ok'
        : 'unknown'

  const makerState: CheckState = !permissions.canViewStaff
    ? 'unknown'
    : maker.isPending
      ? 'pending'
      : maker.isError || maker.data === null
        ? 'unknown'
        : maker.data.status === 'Active'
          ? 'ok'
          : 'blocked'

  const checks: PreflightCheck[] = [
    {
      key: 'two-people',
      state: 'ok',
      label: 'Two different people',
      detail:
        'The queue never contains a request you raised, and the engine refuses a self-approval in the aggregate as well as in the service. Both names reach the trail.',
    },
    {
      key: 'permission',
      state: canApprove ? 'ok' : 'blocked',
      label: canApprove
        ? `You hold ${descriptor.checkerPermission}`
        : `You do not hold ${descriptor.checkerPermission}`,
      detail: canApprove
        ? 'Which is why this request is in your queue: the server filters the inbox by exactly this permission.'
        : 'The server filters the inbox by this permission, so a row here without it means your permissions changed after the page loaded. Reload before acting.',
    },
    {
      key: 'branch',
      state: branchInReach,
      label:
        branchInReach === 'ok'
          ? 'The branch is within your reach'
          : branchInReach === 'blocked'
            ? 'Institution-wide, and you cannot act across branches'
            : 'The branch is not in your granted list',
      detail:
        branchInReach === 'blocked' ? (
          <>
            User administration, role definitions and product configuration belong to no branch, and
            only a checker who may operate across every branch can countersign one. That is the
            intended shape: these decisions are head office&apos;s, not a branch&apos;s.
          </>
        ) : reach.canAccessAllBranches ? (
          <>
            You may act across every branch, so your queue is not limited to the branches listed
            against your account — the server applies no branch filter to it at all.
          </>
        ) : branchInReach === 'ok' ? (
          <>The request was raised at a branch you are granted access to.</>
        ) : (
          <>
            The server put this in your queue, so it accepted your reach for it. This client&apos;s
            branch list simply does not name that branch, which is a gap in the branch directory
            rather than in your access.
          </>
        ),
    },
    {
      key: 'maker',
      state: makerState,
      label:
        makerState === 'ok'
          ? `${approval.makerUsername} is still active`
          : makerState === 'blocked'
            ? `${approval.makerUsername} is ${(maker.data?.status ?? 'not active').toLowerCase()}`
            : `Whether ${approval.makerUsername} is still active is not known here`,
      detail:
        makerState === 'blocked' ? (
          <>
            The engine refuses this approval with{' '}
            <span className="font-mono text-2xs">access.maker_no_longer_active</span>: executing
            their command would be acting on the authority of an account that no longer has any.
            The operation has to be raised again by somebody active.
          </>
        ) : makerState === 'ok' ? (
          <>Checked against the staff directory just now, not against the request.</>
        ) : !permissions.canViewStaff ? (
          <>
            Reading the staff directory needs{' '}
            <span className="font-mono text-2xs">{APPROVAL_PERMISSIONS.staffView}</span>. Without it
            a maker disabled since submission is only discovered by pressing Approve and being
            refused.
          </>
        ) : maker.isPending ? (
          <>Looking them up…</>
        ) : (
          <>
            The directory did not return this username within your branch reach. That is not proof
            the account is gone — it may simply be at a branch you cannot see.
          </>
        ),
    },
    {
      key: 'deadline',
      state: expiry.urgency === 'expired' ? 'blocked' : expiry.urgency === 'unknown' ? 'unknown' : 'ok',
      label:
        expiry.urgency === 'expired'
          ? 'This request has lapsed'
          : expiry.urgency === 'unknown'
            ? 'The deadline could not be read'
            : `${expiry.label}, at ${formatDateTime(approval.expiresAtUtc)}`,
      detail:
        expiry.urgency === 'expired' ? (
          <>
            A lapsed request cannot be countersigned and there is nothing here to extend it. It
            leaves the queue on the next sweep, and the maker submits the operation again.
          </>
        ) : (
          <>
            Balance-sensitive operations are given twelve hours and everything else forty-eight. An
            approval days after submission is one given against facts nobody re-read.
          </>
        ),
    },
  ]

  if (approval.amountMinor !== null || approval.amountBasis === 'unreadable') {
    checks.push({
      key: 'limit',
      state: 'unknown',
      label: 'Your approval ceiling for this operation is not visible here',
      detail: (
        <>
          Approval limits are the institution&apos;s own delegated-authority schedule, held on the
          server with no endpoint that reads them back. If this amount is over yours the
          countersignature is refused with{' '}
          <span className="font-mono text-2xs">access.approval_limit_exceeded</span> and nothing
          changes.
        </>
      ),
    })
  }

  const decided = outcome !== null

  /**
   * Opens a decision dialog on a clean slate.
   *
   * The mutations outlive the dialog — closing it unmounts the form, not the hook — so a refusal
   * from a previous attempt would otherwise be sitting in the new dialog before anything has been
   * submitted. On a screen whose whole job is telling a checker why the engine said no, a stale
   * "your approval ceiling was exceeded" against a fresh press is worse than no message at all.
   */
  const openDialog = (mode: DecisionMode) => {
    approve.reset()
    reject.reset()
    setDialog(mode)
  }

  return (
    <>
      <Sheet
        open
        onOpenChange={(open) => {
          if (!open) {
            onOpenChange(false)
          }
        }}
      >
        <SheetContent aria-describedby={undefined} className="max-w-2xl">
          <SheetHeader>
            <SheetTitle>{descriptor.label}</SheetTitle>
            <SheetDescription>
              <span className="font-mono">{descriptor.code}</span> · {descriptor.moduleLabel} ·
              request <span className="font-mono">{approval.requestId}</span>
            </SheetDescription>
          </SheetHeader>

          <SheetBody className="space-y-4">
            {decided ? (
              <Alert tone={outcome === 'approve' ? 'success' : 'warning'}>
                {outcome === 'approve' ? <ShieldCheck aria-hidden /> : <UserCheck aria-hidden />}
                <div className="space-y-1">
                  <AlertTitle>
                    {outcome === 'approve'
                      ? 'Countersigned — the command has run'
                      : 'Refused — the request is closed'}
                  </AlertTitle>
                  <AlertDescription>
                    {outcome === 'approve' ? (
                      <>
                        The maker&apos;s command was re-dispatched and completed. Whatever it
                        changed — a loan, a till, a ledger period — is changed now, and both names
                        are on the trail. The queue below has already dropped this row.
                      </>
                    ) : (
                      <>
                        Your reason is recorded against your name and is what the maker is told.
                        Nothing was changed. If the operation is still wanted, they raise it again.
                      </>
                    )}
                  </AlertDescription>
                </div>
              </Alert>
            ) : null}

            <div className="space-y-4">
              <TabStrip<DetailTab>
                tabs={
                  [
                    { id: 'request', label: 'The request' },
                    { id: 'history', label: 'History' },
                  ] satisfies TabDefinition<DetailTab>[]
                }
                active={tab}
                onChange={setTab}
                panelId={PANEL_ID}
                label="Approval request sections"
              />

              <div id={PANEL_ID} role="tabpanel" aria-labelledby={`${PANEL_ID}-tab-${tab}`}>
                {tab === 'request' ? (
                  <div className="space-y-4">
                    <div className="rounded-card border border-border-subtle bg-surface-muted p-3">
                      <p className="text-sm text-heading">{approval.description}</p>
                      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3">
                        <Field label="Submitted by" value={approval.makerUsername} />
                        <Field
                          label="Submitted"
                          value={`${formatDateTime(approval.madeAtUtc)} · ${describeWait(approval.madeAtUtc)}`}
                        />
                        <Field
                          label="Branch"
                          value={<BranchCell branchId={approval.branchId} branches={reach.branches} />}
                        />
                        <Field label="Amount" value={<ApprovalAmount approval={approval} />} />
                        <Field label="Lapses" value={<ExpiryBadge expiresAtUtc={approval.expiresAtUtc} />} />
                        <Field
                          label="Countersigned by"
                          value={<span className="font-mono text-xs">{descriptor.checkerPermission}</span>}
                        />
                      </dl>
                    </div>

                    <Alert tone="info">
                      <FileLock2 aria-hidden />
                      <div className="space-y-1">
                        <AlertTitle>The submitted command itself is not served</AlertTitle>
                        <AlertDescription>
                          The maker&apos;s command is stored server-side and hashed, so the
                          countersignature applies to the exact bytes submitted — but the inbox
                          carries only the one-line description above, and no endpoint returns the
                          payload or the record it touches. A side-by-side of what was asked for
                          against the current state cannot be shown from this API. Open the customer,
                          loan, or till it names if you need to see the state it will run against.
                        </AlertDescription>
                      </div>
                    </Alert>

                    <section className="space-y-2">
                      <h2 className="text-xs font-semibold tracking-wide text-muted uppercase">
                        Before you decide
                      </h2>
                      <ul className="space-y-2">
                        {checks.map((check) => (
                          <CheckRow key={check.key} check={check} />
                        ))}
                      </ul>
                    </section>
                  </div>
                ) : (
                  <ApprovalTrail requestId={approval.requestId} enabled={tab === 'history'} />
                )}
              </div>
            </div>
          </SheetBody>

          <SheetFooter>
            {decided ? (
              <Button variant="secondary" onClick={() => onOpenChange(false)}>
                Back to the queue
              </Button>
            ) : (
              <>
                {!canApprove ? (
                  <span className="mr-auto text-xs text-muted">
                    You cannot decide this one — {descriptor.checkerPermission} is required.
                  </span>
                ) : null}
                <Button
                  variant="secondary"
                  disabled={!canApprove || approve.isPending || reject.isPending}
                  onClick={() => openDialog('reject')}
                >
                  <ShieldX />
                  Refuse
                </Button>
                <Button
                  variant="primary"
                  disabled={
                    !canApprove ||
                    approve.isPending ||
                    reject.isPending ||
                    expiry.urgency === 'expired'
                  }
                  onClick={() => openDialog('approve')}
                >
                  {approve.isPending ? <Spinner /> : <ShieldCheck />}
                  Countersign
                </Button>
              </>
            )}
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {dialog !== null ? (
        <DecisionDialog
          open
          onOpenChange={(open) => {
            if (!open) {
              setDialog(null)
            }
          }}
          mode={dialog}
          approval={approval}
          branches={reach.branches}
          isPending={dialog === 'approve' ? approve.isPending : reject.isPending}
          error={dialog === 'approve' ? approve.error : reject.error}
          onConfirm={(text) => {
            if (dialog === 'approve') {
              approve.mutate(
                { requestId: approval.requestId, note: text },
                {
                  onSuccess: () => {
                    setDialog(null)
                    setOutcome('approve')
                  },
                },
              )
              return
            }

            reject.mutate(
              { requestId: approval.requestId, reason: text },
              {
                onSuccess: () => {
                  setDialog(null)
                  setOutcome('reject')
                },
              },
            )
          }}
        />
      ) : null}
    </>
  )
}

const CHECK_ICONS: Record<CheckState, typeof CheckCircle2> = {
  ok: CheckCircle2,
  blocked: XCircle,
  unknown: CircleHelp,
  pending: CircleHelp,
}

const CHECK_TONES: Record<CheckState, string> = {
  ok: 'text-success-text',
  blocked: 'text-danger-text',
  unknown: 'text-muted',
  pending: 'text-muted',
}

/**
 * One pre-flight line.
 *
 * Three states rather than two, and the third is the point: a check the client cannot perform —
 * an approval ceiling, a maker's status without the directory permission — reads as unknown, never
 * as passed. A green tick nobody earned is worse than no tick at all.
 */
function CheckRow({ check }: { check: PreflightCheck }) {
  const Icon = CHECK_ICONS[check.state]

  return (
    <li className="flex gap-2.5 rounded-card border border-border-subtle bg-surface p-3">
      <Icon className={cn('mt-0.5 size-4 shrink-0', CHECK_TONES[check.state])} aria-hidden />
      <div className="min-w-0 space-y-0.5">
        <p className="text-sm font-medium text-heading">{check.label}</p>
        <p className="text-xs text-body">{check.detail}</p>
      </div>
    </li>
  )
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">{label}</dt>
      <dd className="mt-0.5 text-sm break-words text-heading">{value}</dd>
    </div>
  )
}
